import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QrCode, Wallet, FileCode2 } from "lucide-react";
import { api } from "@/lib/api";
import TxTable from "@/components/TxTable";
import {
  AddrLink, Card, CopyBtn, DetailRow, EmptyState, ErrorState, Pager, SkeletonRows,
  StatCard, TokenLogo, TxLink, TypeBadge, WatchButton, usePaged,
} from "@/components/common";
import { fmtCompact, fmtNum, fmtTokenAmount, fmtUsd, shortHash, timeAgo } from "@/lib/format";
import { usePageTitle } from "@/lib/seo";

const TABS = [
  { id: "transactions", label: "Transactions" },
  { id: "token-transfers", label: "Token Transfers" },
  { id: "tokens", label: "Token Balances" },
  { id: "stock-tokens", label: "Stock Tokens" },
  { id: "internal", label: "Internal" },
  { id: "nft", label: "NFT Activity" },
];

const transferSentence = (t, self) => {
  const amt = fmtTokenAmount(t.total?.value, t.total?.decimals ?? t.token?.decimals);
  const sym = t.token?.symbol || t.token?.name || "tokens";
  const out = t.from?.hash?.toLowerCase() === self.toLowerCase();
  return out ? `Transferred ${amt} ${sym} to another wallet` : `Received ${amt} ${sym}`;
};

function TransfersTab({ hash }) {
  const pager = usePaged((p) => api.get(`/address/${hash}/token-transfers`, { params: p }).then((r) => r.data), [hash]);
  if (pager.loading) return <SkeletonRows n={8} />;
  if (pager.error) return <ErrorState />;
  if (!pager.items.length) return <EmptyState title="No token transfers found" />;
  return (
    <>
      <div className="divide-y">
        {pager.items.map((t, i) => (
          <div key={i} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid="transfer-row">
            <span data-testid="transfer-direction-badge" className={`inline-flex text-[10px] font-semibold rounded-md px-1.5 py-0.5 shrink-0 ${
              t.from?.hash?.toLowerCase() === hash.toLowerCase() ? "bg-yellow-500/15 text-yellow-500" : "bg-primary/15 text-primary"
            }`}>{t.from?.hash?.toLowerCase() === hash.toLowerCase() ? "OUT" : "IN"}</span>
            <TokenLogo url={t.token?.icon_url} symbol={t.token?.symbol} size={24} />
            <div className="min-w-0 flex-1">
              <p className="text-sm">{transferSentence(t, hash)}</p>
              <p className="text-[10px] text-muted-foreground font-mono">
                <TxLink hash={t.transaction_hash} chars={5} /> · {timeAgo(t.timestamp)}
              </p>
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <AddrLink hash={t.from?.hash} name={t.from?.name} chars={4} /> → <AddrLink hash={t.to?.hash} name={t.to?.name} chars={4} />
            </div>
          </div>
        ))}
      </div>
      <Pager pager={pager} />
    </>
  );
}

function TxsTab({ hash }) {
  const pager = usePaged((p) => api.get(`/address/${hash}/transactions`, { params: p }).then((r) => r.data), [hash]);
  return (
    <>
      <TxTable items={pager.items} loading={pager.loading} error={pager.error} self={hash} />
      {!pager.loading && !pager.error && pager.items.length > 0 && <Pager pager={pager} />}
    </>
  );
}

function InternalTab({ hash }) {
  const pager = usePaged((p) => api.get(`/address/${hash}/internal`, { params: p }).then((r) => r.data), [hash]);
  if (pager.loading) return <SkeletonRows n={6} />;
  if (pager.error) return <ErrorState />;
  if (!pager.items.length) return <EmptyState title="No internal transactions found" />;
  return (
    <>
      <div className="divide-y">
        {pager.items.map((t, i) => (
          <div key={i} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
            <TypeBadge>{t.type || "call"}</TypeBadge>
            <AddrLink hash={t.from?.hash} chars={5} /> → <AddrLink hash={t.to?.hash} chars={5} />
            <span className="ml-auto font-mono text-xs">{fmtNum(Number(t.value || 0) / 1e18, { maximumFractionDigits: 6 })} ETH</span>
            <span className="text-xs text-muted-foreground">{timeAgo(t.timestamp)}</span>
          </div>
        ))}
      </div>
      <Pager pager={pager} />
    </>
  );
}

function NftTab({ hash }) {
  const pager = usePaged((p) => api.get(`/address/${hash}/nft`, { params: p }).then((r) => r.data), [hash]);
  if (pager.loading) return <SkeletonRows n={4} />;
  if (pager.error) return <ErrorState />;
  if (!pager.items.length) return <EmptyState title="No NFT activity found" />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4">
      {pager.items.map((n, i) => (
        <Card key={i} className="p-3">
          {n.image_url ? <img src={n.image_url} alt="" className="rounded-lg w-full aspect-square object-cover" /> : <div className="rounded-lg w-full aspect-square bg-muted" />}
          <p className="mt-2 text-xs truncate">{n.metadata?.name || `#${n.id}`}</p>
          <p className="text-[10px] text-muted-foreground truncate">{n.token?.name}</p>
        </Card>
      ))}
    </div>
  );
}

function BalancesTab({ balances, stockOnly }) {
  const list = useMemo(() => {
    const items = balances || [];
    return stockOnly ? items.filter((b) => (b.token?.name || "").toLowerCase().includes("robinhood token")) : items;
  }, [balances, stockOnly]);
  if (!list.length)
    return <EmptyState title={stockOnly ? "No stock tokens held" : "No token balances found"} subtitle={stockOnly ? "Robinhood tokenized equities held by this wallet will appear here." : undefined} />;
  return (
    <div className="divide-y">
      {list.map((b, i) => {
        const t = b.token || {};
        const amount = fmtTokenAmount(b.value, t.decimals);
        const usd = t.exchange_rate ? (Number(b.value) / 10 ** Number(t.decimals || 18)) * Number(t.exchange_rate) : null;
        return (
          <Link key={i} to={`/token/${t.address_hash}`} className="flex items-center gap-3 px-4 py-3 table-row-hover" data-testid="balance-row">
            <TokenLogo url={t.icon_url} symbol={t.symbol} size={28} />
            <div className="min-w-0">
              <p className="text-sm truncate">{t.name || shortHash(t.address_hash)}</p>
              <p className="text-[10px] text-muted-foreground uppercase">{t.symbol} · {t.type}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-sm font-mono">{amount}</p>
              <p className="text-[10px] text-muted-foreground">{usd !== null ? fmtUsd(usd) : "Price unavailable"}</p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function ContractTab({ hash }) {
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/contracts/${hash}`).then((r) => setC(r.data)).catch(() => setErr(true));
  }, [hash]);
  if (err) return <EmptyState title="Contract source not available" subtitle="This contract has not been verified on the Robinhood Chain indexer." />;
  if (!c) return <SkeletonRows n={4} />;
  return (
    <div className="p-4 space-y-3">
      <div className="flex flex-wrap gap-2">
        <TypeBadge>{c.is_verified ? "✓ Verified source code" : "Unverified"}</TypeBadge>
        {c.language && <TypeBadge>{c.language}</TypeBadge>}
        {c.compiler_version && <TypeBadge>{c.compiler_version}</TypeBadge>}
        {c.name && <TypeBadge>{c.name}</TypeBadge>}
      </div>
      {c.source_code ? (
        <pre className="text-[11px] font-mono bg-secondary rounded-lg p-4 overflow-auto max-h-[480px] whitespace-pre-wrap">{c.source_code}</pre>
      ) : (
        <p className="text-xs text-muted-foreground">Source code has not been published for this contract.</p>
      )}
    </div>
  );
}

export default function AddressPage() {
  const { hash } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("transactions");
  const [showQr, setShowQr] = useState(false);
  usePageTitle(info ? `${info.is_contract ? "Contract" : "Wallet"} ${shortHash(hash)}` : "Address", `Balance, tokens and transaction history for ${hash} on Robinhood Chain.`);

  useEffect(() => {
    setInfo(null); setErr(false); setTab("transactions");
    api.get(`/address/${hash}`).then((r) => setInfo(r.data)).catch(() => setErr(true));
  }, [hash]);

  if (err) return <ErrorState message="We could not find this address on Robinhood Chain. Please check the value and try again." />;
  if (!info) return <SkeletonRows n={8} h="h-14" />;

  const isContract = info.is_contract;
  const tabs = isContract ? [...TABS, { id: "contract", label: "Contract" }] : TABS;

  return (
    <div className="space-y-6 pt-8">
      <div className="flex flex-wrap items-start gap-4">
        <span className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          {isContract ? <FileCode2 size={20} /> : <Wallet size={20} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
              {info.name || (isContract ? "Smart Contract" : "Wallet")}
            </h1>
            {info.gmscan_label && <TypeBadge>{info.gmscan_label}</TypeBadge>}
            {isContract && <TypeBadge>{info.is_verified ? "Verified Contract" : "Contract"}</TypeBadge>}
          </div>
          <div className="mt-1 flex items-center gap-2 min-w-0">
            <p className="font-mono text-xs text-muted-foreground truncate" data-testid="address-hash">{hash}</p>
            <CopyBtn text={hash} testId="copy-address-btn" />
            <button data-testid="qr-btn" onClick={() => setShowQr(!showQr)} className="text-muted-foreground hover:text-primary transition-colors duration-150"><QrCode size={13} /></button>
          </div>
          {showQr && (
            <img data-testid="qr-image" className="mt-3 rounded-lg border bg-white p-2" width={140} height={140} alt="QR"
              src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${hash}`} />
          )}
        </div>
        <WatchButton type={isContract ? "contract" : "wallet"} value={hash} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-eth-balance" label="ETH Balance" value={`${fmtNum(info.eth_balance, { maximumFractionDigits: 6 })} ETH`} sub={fmtUsd(info.eth_balance * info.eth_usd_rate)} />
        <StatCard testId="stat-portfolio" label="Est. Portfolio Value" value={fmtUsd(info.portfolio_value_usd)} sub="ETH + priced tokens" />
        <StatCard testId="stat-tx-count" label="Transactions" value={fmtCompact(info.counters?.transactions_count)} sub={`${fmtCompact(info.counters?.token_transfers_count)} token transfers`} />
        <StatCard testId="stat-tokens-held" label="Tokens Held" value={fmtNum(info.token_balances?.length ?? 0, { maximumFractionDigits: 0 })} sub={isContract ? "Contract account" : "Wallet account"} />
      </div>

      {isContract && (info.creator_address_hash || info.creation_transaction_hash) && (
        <Card className="p-4">
          <DetailRow label="Contract Creator"><AddrLink hash={info.creator_address_hash} chars={10} /></DetailRow>
          <DetailRow label="Creation Transaction">{(info.creation_transaction_hash || info.creation_tx_hash) ? <TxLink hash={info.creation_transaction_hash || info.creation_tx_hash} chars={12} /> : "—"}</DetailRow>
        </Card>
      )}

      <Card>
        <div className="flex gap-1 overflow-x-auto border-b px-2 pt-2">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-xs whitespace-nowrap rounded-t-lg transition-colors duration-150 ${
                tab === t.id ? "text-primary border-b-2 border-primary font-semibold" : "text-muted-foreground hover:text-foreground"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === "transactions" && <TxsTab hash={hash} />}
        {tab === "token-transfers" && <TransfersTab hash={hash} />}
        {tab === "tokens" && <BalancesTab balances={info.token_balances} />}
        {tab === "stock-tokens" && <BalancesTab balances={info.token_balances} stockOnly />}
        {tab === "internal" && <InternalTab hash={hash} />}
        {tab === "nft" && <NftTab hash={hash} />}
        {tab === "contract" && <ContractTab hash={hash} />}
      </Card>
    </div>
  );
}
